import React from "react";
import PropTypes from "prop-types";

import { maskJoinedPhone } from "util/maskPhone";

/**
 * Card · Patient details (editable) — Figma 3031:9505. Every field is the
 * patient RECORD's (or its latest anthropometric reading), shown read-only; the
 * header's "Update" opens the same Patient-details dialog as the patient
 * header's name, and a save there re-reads the record, so the card and the
 * printed document follow. The owner mounts that dialog (see ReviewPrescription)
 * — this card only asks for it.
 *
 * The board's footer "Patient details is system-assigned and cannot be edited"
 * is NOT drawn: it contradicts the Update button beside it, and it is the note
 * 647f671 removed on purpose.
 */
const PatientDetailsCard = ({ values, onChange, onUpdate, onWeightClick }) => (
  <section className="rx-card">
    <header className="rx-card-head">
      <h3 className="rx-card-title">Patient details</h3>
      <span className="rx-card-meta">· editable — changes reflect on the prescription</span>
      {onUpdate ? (
        /* "DA / Button · toolbar · plus" (6326:7026) with its plus hidden —
           the same component .rx-add implements for "+ Add diagnosis". */
        <div className="rx-card-head-end">
          <button type="button" className="rx-add" aria-label="Update patient details" onClick={onUpdate}>
            Update
          </button>
        </div>
      ) : null}
    </header>

    <div className="rx-grid-2">
      <div className="rx-field">
        <label className="rx-field-label" htmlFor="rx-fullname">
          Full name
        </label>
        <span className="rx-field-wrap">
          <input
            id="rx-fullname"
            type="text"
            className="rx-input"
            value={values.fullName}
            readOnly
            tabIndex={-1}
            title="From the patient record"
          />
        </span>
      </div>

      <div className="rx-field">
        <label className="rx-field-label" htmlFor="rx-age">
          Age
        </label>
        <span className="rx-field-wrap">
          {/* Derived from the patient's date of birth, so it cannot drift from
              the record or go stale as the patient gets older. Read-only rather
              than hidden: it is printed on the prescription, so it has to be
              visible and checkable. */}
          <input
            id="rx-age"
            type="text"
            className="rx-input has-suffix"
            value={values.ageMonths || values.age}
            readOnly
            tabIndex={-1}
            title="Calculated from the patient's date of birth"
          />
          {/* Months under a year — the unit follows the number, or an infant
              reads as a six-year-old. */}
          <span className="rx-suffix">{values.ageMonths ? "mo" : "yr"}</span>
        </span>
      </div>

      <div className="rx-field">
        <label className="rx-field-label" htmlFor="rx-gender">
          Gender
        </label>
        <span className="rx-field-wrap">
          {/* From the patient record. Editing it here would have changed only
              this prescription's copy, leaving the printed document disagreeing
              with the record it was drawn from. */}
          <input
            id="rx-gender"
            type="text"
            className="rx-input"
            value={values.gender}
            readOnly
            tabIndex={-1}
            title="From the patient record"
          />
        </span>
      </div>

      <div className="rx-field">
        <label className="rx-field-label" htmlFor="rx-weight">
          Weight
        </label>
        {/* The patient's latest Anthropometric reading, shown read-only like
            age and gender. A weight typed here would have changed only this
            prescription's copy, leaving the printed document disagreeing with
            the record it was drawn from — a fresh weigh-in belongs in
            Anthropometric data, which this then reflects. */}
        {/* MOBILE ONLY, when the host passes onWeightClick: the value becomes a
            button that opens Anthropometric data's own Weight sheet on this
            screen, so a fresh weigh-in is recorded where it belongs and this
            field follows it. Without the prop — desktop — the field below is
            exactly the read-only input it always was. */}
        <span className="rx-field-wrap">
          {onWeightClick ? (
            <button
              type="button"
              id="rx-weight"
              className="rx-input has-suffix rx-weight-edit"
              onClick={onWeightClick}
              aria-label={`Weight${values.weight ? ` ${values.weight} kg` : ""} — update`}
            >
              {values.weight}
            </button>
          ) : (
            <input
              id="rx-weight"
              type="text"
              className="rx-input has-suffix"
              value={values.weight}
              readOnly
              tabIndex={-1}
              title="From the patient's latest anthropometric reading"
            />
          )}
          <span className="rx-suffix">kg</span>
        </span>
      </div>


      <div className="rx-field is-wide">
        <label className="rx-field-label" htmlFor="rx-phone">
          Phone
        </label>
        <span className="rx-field-wrap">
          {/* The patient's registered number — not editable from a prescription,
              and shown masked ("+91 XXXXX X3210"); values.phone arrives joined
              ("+91-9876543210"). */}
          <input
            id="rx-phone"
            type="text"
            className="rx-input"
            value={maskJoinedPhone(values.phone)}
            readOnly
            title="The patient's registered phone number can't be changed here"
          />
        </span>
      </div>
    </div>
  </section>
);
PatientDetailsCard.propTypes = {
  values: PropTypes.shape({
    fullName: PropTypes.string.isRequired,
    age: PropTypes.string.isRequired,
    gender: PropTypes.string.isRequired,
    weight: PropTypes.string.isRequired,
    phone: PropTypes.string.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  /** Opens the Patient-details dialog. Omit and the card has no Update button. */
  onUpdate: PropTypes.func,
  /** Mobile only: makes Weight a button that opens the anthropometric Weight
      sheet. Omit (desktop) and Weight stays a read-only input. */
  onWeightClick: PropTypes.func,
};

export default PatientDetailsCard;
